import { differenceInHours } from 'date-fns';

import { UPCOMING_SESSION_HOURS_THRESHOLD } from '../../home/components/guidance/NextGuidanceSessionWidget';
import { usePreMoneyMapCompleteness } from '../../onboarding/hooks/usePreMoneyMapCompleteness';
import { GuidanceSessionType } from '../types/GuidanceSessions';
import { useGetNextGuidanceSession } from './useGetNextGuidanceSession';

/**
 * Display logic for guidance session widgets on the home page.
 */
export const useNextGuidanceSessionOnHomeDisplay = () => {
    const {
        nextGuidanceSession,
        nextGuidanceOnboardingSessionType,
        nextGuidanceOnboardingSessionNeedsBooking,
    } = useGetNextGuidanceSession();

    const { isPreMoneyMapComplete } = usePreMoneyMapCompleteness();

    /** Whether or not to show an upcoming Guidance Session in the Home hero
     * Currently true if there is an upcoming Discovery or Roadmap session, or if any session is upcoming within 48 hours.
     */
    const shouldShowHeroGuidanceSessionWidget =
        nextGuidanceSession != null &&
        (nextGuidanceSession.type === GuidanceSessionType.DISCOVERY ||
            nextGuidanceSession.type === GuidanceSessionType.ROADMAP ||
            differenceInHours(
                new Date(nextGuidanceSession.startTime),
                new Date(),
            ) <= UPCOMING_SESSION_HOURS_THRESHOLD);

    /**
     * Whether or not to show booking a Guidance Session in the Home hero
     * Currently true if the next session type is Discovery or Roadmap + they have completed pre-Money Map checks.
     */
    const shouldShowHeroBookingWidget =
        (nextGuidanceOnboardingSessionNeedsBooking === true &&
            nextGuidanceOnboardingSessionType ===
                GuidanceSessionType.DISCOVERY) ||
        (nextGuidanceOnboardingSessionNeedsBooking === true &&
            isPreMoneyMapComplete &&
            nextGuidanceOnboardingSessionType === GuidanceSessionType.ROADMAP);

    const isDiscoveryOrMoneyMap =
        nextGuidanceOnboardingSessionType === GuidanceSessionType.DISCOVERY ||
        (nextGuidanceOnboardingSessionType === GuidanceSessionType.ROADMAP &&
            isPreMoneyMapComplete);

    /**
     *  Whether or not to show widget for booking guidance session in the Home Heads Up section.
     *  Used as an alternative to Home Hero on smaller screens.
     *  Currently true if the member needs to book a session that is not Discovery or Money Map.
     */
    const shouldShowHeadsUpBookingWidget =
        nextGuidanceOnboardingSessionNeedsBooking === true &&
        nextGuidanceOnboardingSessionType != null &&
        !isDiscoveryOrMoneyMap;

    /**
     * Controls whether or not the Guidance session widget appears first in the Home Hero.
     * It appears first if there is an upcoming session, based on shouldShowHeroGuidanceSessionWidget.
     * It appears second if the member needs to book a session.
     */
    const heroFlexDirection: 'column' | 'column-reverse' =
        shouldShowHeroGuidanceSessionWidget ? 'column' : 'column-reverse';

    /**
     * Whether or not to render the Guidance session widget, for either an upcoming session or for booking a session, in the Home hero
     */
    const shouldShowHeroSessionWidget =
        shouldShowHeroGuidanceSessionWidget || shouldShowHeroBookingWidget;

    return {
        shouldShowHeroSessionWidget,
        shouldShowHeroGuidanceSessionWidget,
        shouldShowHeroBookingWidget,
        shouldShowHeadsUpBookingWidget,
        heroFlexDirection,
    };
};
